import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

/** Respiro entre a base do painel e o topo do rodapé. */
const GAP_PX = 16;
/** Piso de segurança: numa tela minúscula o painel não colapsa (a lista de mensagens rola por dentro). */
const MIN_HEIGHT_PX = 320;
/**
 * Teto: com a lista enorme o console não acompanha o tamanho dela nem num monitor alto.
 * Passou do teto, ele para de crescer e fica flutuando (sticky): a lista rola e o console
 * acompanha na tela. Sempre vale o menor entre o disponível acima do rodapé e este teto.
 */
const MAX_HEIGHT_PX = 720;
/** Largura a partir da qual o painel fica ao lado da lista e sticky (`lg` do Tailwind). */
const SIDE_BY_SIDE_MIN_WIDTH_PX = 1024;
/** Empilhado não é sticky: reserva a barra do topo para o painel caber na tela quando alcançado. */
const STACKED_TOP_RESERVE_PX = 96;

/**
 * Altura de um painel que nunca passa por baixo do rodapé fixo da etapa.
 *
 * A altura sai da posição real: `topo do rodapé - topo do painel - folga` (sem rodapé,
 * a altura da janela faz de limite). Como o painel é sticky, ao rolar o topo dele sobe
 * e a altura cresce sozinha. Recalcula no resize, no scroll e quando o conteúdo acima
 * do painel ou o próprio rodapé mudam de tamanho (o alerta de aba bloqueada aparece e
 * some). O scroll é do `<main>`, não da janela, e scroll não borbulha: por isso o
 * listener fica no `document`, em captura.
 *
 * O rodapé entra como elemento (estado da tela, via callback ref), não como ref-objeto:
 * ele pode montar depois do painel, e só a mudança de estado refaz a medição e põe o
 * rodapé no ResizeObserver. Com ref-objeto a primeira medição via `null` e caía na
 * janela, deixando o input meio por baixo do rodapé.
 *
 * A altura fica entre o mínimo (320px) e o máximo (720px). O teto vale também empilhado.
 *
 * Abaixo de `lg` o painel fica empilhado e sem sticky; o topo dele não diz nada sobre
 * o que cabe na tela, então a altura é o limite menos a reserva da barra do topo.
 */
export function useFitAboveFooter(footer: HTMLElement | null): {
  ref: RefObject<HTMLDivElement>;
  height: number | undefined;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  const measure = useCallback(() => {
    const panel = ref.current;
    if (!panel) return;
    const limit = footer?.getBoundingClientRect().top ?? window.innerHeight;
    const available =
      window.innerWidth < SIDE_BY_SIDE_MIN_WIDTH_PX
        ? limit - STACKED_TOP_RESERVE_PX
        : limit - panel.getBoundingClientRect().top - GAP_PX;
    const next = Math.min(MAX_HEIGHT_PX, Math.max(MIN_HEIGHT_PX, Math.round(available)));
    setHeight((current) => (current === next ? current : next));
  }, [footer]);

  // Antes da pintura, para o painel não aparecer com a altura errada.
  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    document.addEventListener("scroll", measure, { capture: true, passive: true });
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      // O conteúdo acima do painel (o alerta amarelo) empurra o topo dele; o rodapé muda de altura.
      const content = ref.current?.parentElement?.parentElement;
      if (content) observer.observe(content);
      if (footer) observer.observe(footer);
    }
    return () => {
      window.removeEventListener("resize", measure);
      document.removeEventListener("scroll", measure, { capture: true });
      observer?.disconnect();
    };
  }, [measure, footer]);

  return { ref, height };
}
